import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Platform } from '@app/classes';
import { FeatureFlag } from '@app/enums';
import { FeatureGuard, NavigationGuard, PermissionGuard } from '@app/guards';
import { RoutesWithOptions } from '@app/interfaces';
import { ApplicantTrackerResolver } from '@applicant-tracker/applicant-tracker.resolver';
import {
    ApplicantEmailShowView,
    ApplicantIndexView,
    ApplicantIndividualMessageView,
    ApplicantMessageStoreView,
    ApplicantMessageTemplateIndexView,
    ApplicantMessageTemplateStoreView,
    ApplicantShowView,
    ApplicantStoreView,
    AutomatedEmailSettingsStoreView,
    BulkApplicantMessageStoreView,
    FunnelMessageTemplateStoreView,
    FunnelMessageTemplateUpdateView,
    FunnelTemplateIndex,
    FunnelTemplateStore,
    FunnelTemplateUpdate,
    InformationShowView,
    InterviewsIndexView,
    InterviewsStoreComponent,
    JobBoardSettingsShowView,
    JobFunnelMessageTemplateStoreView,
    JobFunnelMessageTemplateUpdateView,
    JobPostingApplicantIndexView,
    JobPostingApplicantInterviewsShowView,
    JobPostingIndexView,
    JobPostingInterviewsIndexView,
    JobPostingInterviewsShowView,
    JobPostingInterviewsStoreView,
    JobPostingStoreView,
    ManageInterviewView,
    PipelineShowView,
    SettingsShowView,
} from '@applicant-tracker/views';
import { ApplicationComponent, DocumentsAndLinksComponent, EmailsComponent, QuestionsComponent } from './components';
import { CommentsComponent } from './components/comments/comments.component';
import { InterviewsComponent } from './components/interviews/interviews.component';
import { DraftJobPostingRedirectGuard } from './guards/draft-job-posting.guard';
import { JobPostingInterviewsUpdateView } from './views/job-posting/interviews/update/job-posting-interviews-update.view';
import { ManageHiringTeamView } from './views/job-posting/manage-hiring-team/manage-hiring-team.view';
import { JobPostingReportsShowView } from './views/job-posting/reports/show/job-posting-reports-show.view';
import { AutomatedEmailSettingsUpdateView } from './views/settings/automated-email-settings/update/automated-email-settings-update.view';

const routes: RoutesWithOptions = [
    { path: '', redirectTo: '/recruiting/jobPostings', pathMatch: 'full' },
    {
        path: 'applicant-message',
        children: [
            {
                path: '',
                component: ApplicantMessageStoreView,
            },
            {
                path: ':jobPostingId',
                component: ApplicantMessageStoreView,
            },
        ],
    },
    {
        path: 'bulk-applicant-message',
        children: [
            {
                path: '',
                component: BulkApplicantMessageStoreView,
                canDeactivate: [NavigationGuard],
            },
            {
                path: ':jobPostingId',
                component: BulkApplicantMessageStoreView,
                canDeactivate: [NavigationGuard],
            },
        ],
    },
    {
        path: 'jobPostings',
        children: [
            {
                path: '',
                component: JobPostingIndexView,
            },
            {
                path: 'create',
                component: JobPostingStoreView,
                canDeactivate: [NavigationGuard],
            },
            {
                path: ':jobPosting',
                resolve: {
                    models: ApplicantTrackerResolver,
                },
                runGuardsAndResolvers: 'always',
                canActivate: [DraftJobPostingRedirectGuard],
                children: [
                    {
                        path: '',
                        redirectTo: 'applicants',
                        pathMatch: 'prefix',
                    },
                    {
                        path: 'information',
                        children: [
                            {
                                path: '',
                                component: InformationShowView,
                            },
                            {
                                path: 'manage-hiring-team',
                                component: ManageHiringTeamView,
                                canDeactivate: [NavigationGuard],
                            },
                        ],
                    },
                    {
                        path: 'interviews',
                        children: [
                            {
                                path: '',
                                component: JobPostingInterviewsIndexView,
                            },
                            {
                                path: 'create',
                                component: JobPostingInterviewsStoreView,
                            },
                            {
                                path: 'manage',
                                component: ManageInterviewView,
                            },
                            {
                                path: 'existing/:interview',
                                component: JobPostingInterviewsStoreView,
                                resolve: {
                                    models: ApplicantTrackerResolver,
                                },
                            },
                            {
                                path: ':interview',
                                children: [
                                    {
                                        path: '',
                                        component: JobPostingInterviewsShowView,
                                        resolve: {
                                            models: ApplicantTrackerResolver,
                                        },
                                    },
                                ],
                            },

                            {
                                path: 'applicantInterviews/:applicantInterview',
                                component: JobPostingApplicantInterviewsShowView,
                                resolve: {
                                    models: ApplicantTrackerResolver,
                                },
                            },
                            {
                                path: ':interview/edit',
                                component: JobPostingInterviewsUpdateView,
                                resolve: {
                                    models: ApplicantTrackerResolver,
                                },
                            },
                        ],
                    },
                    {
                        path: 'reports',
                        canActivate: [PermissionGuard],
                        data: {
                            permission: Platform.recruiting.permission.recruiter,
                        },
                        children: [
                            {
                                path: '',
                                component: JobPostingReportsShowView,
                            },
                        ],
                    },
                    {
                        path: 'pipeline',
                        children: [
                            {
                                path: '',
                                component: PipelineShowView,
                            },
                            {
                                path: ':jobFunnel',
                                resolve: {
                                    models: ApplicantTrackerResolver,
                                },
                                children: [
                                    {
                                        path: 'jobFunnelMessageTemplate',
                                        children: [
                                            {
                                                path: 'create',
                                                component: JobFunnelMessageTemplateStoreView,
                                                canDeactivate: [NavigationGuard],
                                            },
                                            {
                                                path: 'edit/:jobFunnelMessageTemplate',
                                                canDeactivate: [NavigationGuard],
                                                resolve: {
                                                    models: ApplicantTrackerResolver,
                                                },
                                                component: JobFunnelMessageTemplateUpdateView,
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: 'settings',
                        resolve: {
                            models: ApplicantTrackerResolver,
                        },
                        component: SettingsShowView,
                    },

                    {
                        path: 'applicants',
                        component: JobPostingApplicantIndexView,
                    },
                ],
            },
        ],
    },
    {
        path: 'applicants',
        children: [
            {
                path: '',
                component: ApplicantIndexView,
            },
            {
                path: 'create',
                component: ApplicantStoreView,
            },
            {
                path: ':applicant',
                resolve: {
                    models: ApplicantTrackerResolver,
                },
                children: [
                    {
                        path: '',
                        component: ApplicantShowView,
                        children: [
                            {
                                path: '',
                                component: ApplicationComponent,
                            },
                            {
                                path: 'questions',
                                component: QuestionsComponent,
                            },
                            {
                                path: 'documents-and-links',
                                component: DocumentsAndLinksComponent,
                            },
                            {
                                path: 'comments',
                                component: CommentsComponent,
                            },
                            {
                                path: 'interviews',
                                component: InterviewsComponent,
                            },
                            {
                                path: 'emails',
                                component: EmailsComponent,
                            },
                        ],
                    },
                    {
                        path: 'emails/:email',
                        resolve: {
                            models: ApplicantTrackerResolver,
                        },
                        component: ApplicantEmailShowView,
                    },
                    {
                        path: 'message',
                        component: ApplicantIndividualMessageView,
                        canDeactivate: [NavigationGuard],
                    },
                ],
            },
        ],
    },
    {
        path: 'templates',
        children: [
            {
                path: 'applicantMessageTemplates',
                children: [
                    {
                        path: '',
                        component: ApplicantMessageTemplateIndexView,
                    },
                    {
                        path: 'create',
                        component: ApplicantMessageTemplateStoreView,
                    },
                    {
                        path: ':applicantMessageTemplate',
                        resolve: {
                            models: ApplicantTrackerResolver,
                        },
                        component: ApplicantMessageTemplateStoreView,
                    },
                ],
            },
            {
                path: 'funnelTemplates',
                children: [
                    {
                        path: '',
                        component: FunnelTemplateIndex,
                    },
                    {
                        path: 'create',
                        component: FunnelTemplateStore,
                    },
                    {
                        path: 'edit/:funnelTemplate',
                        resolve: {
                            models: ApplicantTrackerResolver,
                        },
                        children: [
                            { path: '', component: FunnelTemplateUpdate },
                            {
                                path: 'funnelTemplateStage/:funnelTemplateStage',
                                resolve: {
                                    models: ApplicantTrackerResolver,
                                },
                                children: [
                                    {
                                        path: 'funnelMessageTemplate',
                                        children: [
                                            {
                                                path: 'create',
                                                component: FunnelMessageTemplateStoreView,
                                                canDeactivate: [NavigationGuard],
                                            },
                                            {
                                                path: 'edit/:funnelMessageTemplate',
                                                canDeactivate: [NavigationGuard],
                                                resolve: {
                                                    models: ApplicantTrackerResolver,
                                                },
                                                component: FunnelMessageTemplateUpdateView,
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        path: 'settings',
        canActivate: [PermissionGuard],
        data: {
            permission: [...Platform.permission.administrate, Platform.recruiting.permission.recruiter],
        },
        resolve: {
            models: ApplicantTrackerResolver,
        },
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'jobBoard',
            },
            {
                path: 'jobBoard',
                component: JobBoardSettingsShowView,
            },
            {
                path: 'automatedEmail',
                canActivate: [FeatureGuard],
                data: {
                    feature: {
                        flags: [FeatureFlag.automatedEmailSettings],
                    },
                },
                children: [
                    {
                        path: 'setUp',
                        component: AutomatedEmailSettingsStoreView,
                    },
                    {
                        path: 'edit',
                        component: AutomatedEmailSettingsUpdateView,
                    },
                ],
            },
        ],
    },
    {
        path: 'interviews',
        canActivate: [PermissionGuard],
        data: {
            permission: [Platform.recruiting.permission.access],
        },
        resolve: {
            models: ApplicantTrackerResolver,
        },
        children: [
            {
                path: '',
                component: InterviewsIndexView,
            },
            {
                path: 'manage/:applicantInterview',
                component: InterviewsStoreComponent,
                resolve: {
                    models: ApplicantTrackerResolver,
                },
            },
        ],
    },
    {
        path: 'pipeline',
        redirectTo: 'recruiting/jobPostings',
        pathMatch: 'full',
    },
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class ApplicantTrackerRouterModule {}
